import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { Action, BlueprintDeploymentState } from "types/generated";

import { isDeploymentUpdateDisabled, getUpdateDisabledTooltip } from "./helpers";

const UPDATE_TEMPLATE_DEPLOYMENT_ACCESS_TYPES = [
  Action.SpaceAdmin,
  Action.UpgradeBlueprintDeployment,
];

/**
 * Hook that returns a function to check if current user can update a specific template deployment
 * User needs to be an admin or have SpaceAdmin or UpgradeBlueprintDeployment action in the specified space
 * Also checks if deployment state allows updates (not Destroying, InProgress, or Unconfirmed)
 * @returns Function that takes spaceId and optional state, returns AccessResult with result and optional message
 */
const useCanUpdateTemplateDeployment = (): ((
  spaceId?: string,
  state?: BlueprintDeploymentState
) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (spaceId?: string, state?: BlueprintDeploymentState) => {
      if (!spaceId) {
        return { result: false };
      }

      // First check permissions
      const hasPermission =
        viewer.admin ||
        hasAtLeastOneRequiredAction(UPDATE_TEMPLATE_DEPLOYMENT_ACCESS_TYPES, viewer, spaceId);

      if (!hasPermission) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      // Then check if deployment state allows updates
      if (state && isDeploymentUpdateDisabled(state)) {
        return { result: false, message: getUpdateDisabledTooltip(state) };
      }

      return { result: true };
    },
    [viewer]
  );
};

export default useCanUpdateTemplateDeployment;
